import { supabase } from "@/integrations/supabase/client";

interface EdgeFunctions {
  validate: (body: any) => Promise<any>;
  generate: (body: any) => Promise<any>;
  analyze?: (body: any) => Promise<any>;
  /** Analyze a single attachment (photo / plan / doc) — Stage 2. */
  analyzeAttachment: (body: {
    kind: 'photo' | 'plan' | 'doc';
    file: { name: string; url?: string; data?: string; mimeType?: string; category?: string };
    runId?: string;
    fileIndex?: number;
  }) => Promise<any>;
  /** Cross-reference all insights for a run — Stage 3. */
  correlateInsights: (runId: string) => Promise<any>;
  exportDoc: (body: any, format?: 'docx' | 'xlsx') => Promise<any>;
}

/**
 * Configuration for generator routing - passed from useGeneratorConfig
 */
export interface GeneratorRoutingConfig {
  slug: string;
  use_generic_pipeline: boolean;
  supported_export_formats: string[];
  standards_behavior?: {
    requires_activity_analysis?: boolean;
  };
}

/**
 * Hook to get edge function callers for a specific generator.
 * 
 * Architecture:
 * - Validation: `validate-prompt` (universal)
 * - Generation: `generate-document` (all generators use generic pipeline)
 * - Export: `export-docx` or `export-xlsx` based on generator's supported_export_formats
 * - Analysis: `analyze-prompt` (when standards_behavior.requires_activity_analysis=true)
 * 
 * Routing is database-driven via the generators table configuration.
 */
export function useEdgeFunctions(config: GeneratorRoutingConfig): EdgeFunctions {
  const { slug, use_generic_pipeline, supported_export_formats, standards_behavior } = config;
  
  return {
    /**
     * Validate user input before generation.
     * All generators use the universal validate-prompt function.
     */
    validate: async (body: any) => {
      return supabase.functions.invoke('validate-prompt', {
        body: { ...body, generatorSlug: slug },
      });
    },

    /**
     * Generate document.
     * Uses database-driven routing via use_generic_pipeline flag.
     * Wraps country/stateProvince in context object for generate-document compatibility.
     */
    generate: async (body: any) => {
      const functionName = use_generic_pipeline
        ? 'generate-document'
        : `generate-${slug}`;

      // Extract context fields and wrap them for generate-document API compatibility
      const { country, stateProvince, projectName, projectNumber, standardReference, specificationSection, contractNumber, methodDescription, ...rest } = body;
      
      const requestBody = {
        ...rest,
        generatorSlug: slug,
        context: {
          country,
          stateProvince,
          projectName,
          projectNumber,
          standardReference,
          specificationSection,
          contractNumber,
          methodDescription,
        },
      };

      return supabase.functions.invoke(functionName, {
        body: requestBody,
      });
    },

    /**
     * Analyze prompt (optional).
     * Only available when standards_behavior.requires_activity_analysis is true.
     */
    analyze: standards_behavior?.requires_activity_analysis
      ? async (body: any) => {
          return supabase.functions.invoke('analyze-prompt', {
            body: { ...body, generatorSlug: slug },
          });
        }
      : undefined,

    /**
     * Analyze a single attachment (Stage 2).
     * Routes through the universal `analyze-attachments` edge function.
     */
    analyzeAttachment: async ({ kind, file, runId, fileIndex }) => {
      return supabase.functions.invoke('analyze-attachments', {
        body: { generatorSlug: slug, kind, file, runId, fileIndex },
      });
    },

    /**
     * Cross-reference all insights for a generation run (Stage 3).
     * Returns { correlations, newHazards, contradictions, persistedCount }.
     */
    correlateInsights: async (runId: string) => {
      return supabase.functions.invoke('correlate-insights', {
        body: { runId, generatorSlug: slug },
      });
    },

    /**
     * Export document to DOCX or XLSX format.
     * Format determined by generator's supported_export_formats column.
     * Can also be explicitly specified via the format parameter.
     */
    exportDoc: async (body: any, format?: 'docx' | 'xlsx') => {
      // If format explicitly specified, use it; otherwise infer from supported formats
      const exportFormat = format ?? 
        (supported_export_formats.includes('xlsx') ? 'xlsx' : 'docx');
      
      const functionName = exportFormat === 'xlsx' ? 'export-xlsx' : 'export-docx';

      return supabase.functions.invoke(functionName, {
        body: { ...body, generatorSlug: slug },
      });
    },
  };
}

